/**
 * Inline email detail panel (body + metadata + attachments)
 * Reuses logic from EmailViewModal for displaying full message.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail01, Paperclip, File06, Download01, FileCheck02, ChevronDown, ChevronUp, XClose, Link03, CheckCircle, XCircle, AlertTriangle, Image01 } from "@untitledui/icons";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { EmailBodyFrame } from "@/components/application/email/email-body-frame";
import { extractBodyContent } from "@/components/application/email/email-body-utils";
import {
    useEmailMessage,
    useMarkEmailAsNotInContext,
    useAssociateEmailToContainer,
    getEmailAttachmentDownloadUrl,
    useSetEmailAttachmentExcluded,
} from "@/hooks/api/use-email-accounts";
import { useReviewMessage } from "@/hooks/api/use-email-limbo";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { ModalOverlay, Modal, Dialog } from "@/components/application/modals/modal";
import { AssociateContainerModal } from "@/components/application/email/associate-container-modal";
import { ApproveEmailModal } from "@/components/application/email/approve-email-modal";
import { MarkNotInContextModal } from "@/components/application/email/mark-not-in-context-modal";
import { toast } from "sonner";
import type { EmailAttachmentResponse } from "@/types/api/email";
import { EmailMessageStatus } from "@/types/api/email";

function formatFileSize(bytes: number | null): string {
    if (bytes === null || bytes === 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateString: string | null): string {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString();
}

const MIN_BODY_HEIGHT_PX = 320;
const DEFAULT_BODY_HEIGHT_PX = 420;
/** Height of the resize handle (padding + grip) so content area = bodyHeight - RESIZE_HANDLE_HEIGHT */
const RESIZE_HANDLE_HEIGHT_PX = 36;

const IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
const isImageType = (ct: string | null) => ct && IMAGE_TYPES.some((t) => ct.toLowerCase().startsWith(t.split("/")[0]));
const isPdfType = (ct: string | null) => ct && ct.toLowerCase().includes("pdf");

function AttachmentItem({
    messageId,
    attachment,
    tEmail,
}: {
    messageId: string;
    attachment: EmailAttachmentResponse;
    tEmail: (key: string, opts?: { defaultValue?: string }) => string;
}) {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [loadingUrl, setLoadingUrl] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const setExcluded = useSetEmailAttachmentExcluded();

    const hasFile = !!(attachment.storage_path ?? attachment.gcs_path);
    const isImage = isImageType(attachment.content_type);
    const isPdf = isPdfType(attachment.content_type);
    const excluded = !!attachment.user_excluded;

    useEffect(() => {
        if (!hasFile || !isImage) return;
        let cancelled = false;
        getEmailAttachmentDownloadUrl(messageId, attachment.attachment_id, 1)
            .then((res) => {
                if (!cancelled) setThumbnailUrl(res.download_url);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [messageId, attachment.attachment_id, hasFile, isImage]);

    const fetchAndOpen = async (action: "download" | "preview") => {
        if (!hasFile) return;
        setLoadingUrl(true);
        try {
            const res = await getEmailAttachmentDownloadUrl(messageId, attachment.attachment_id, 1);
            if (action === "download") {
                window.open(res.download_url, "_blank", "noopener,noreferrer");
            } else if (action === "preview" && isImage) {
                setPreviewUrl(res.download_url);
                setPreviewOpen(true);
            } else if (action === "preview" && isPdf) {
                window.open(res.download_url, "_blank", "noopener,noreferrer");
            }
        } catch {
            toast.error(tEmail("emailView.attachmentDownloadError", { defaultValue: "Impossibile aprire l'allegato" }));
        } finally {
            setLoadingUrl(false);
        }
    };

    const handleSetExcluded = (excluded: boolean) => {
        setExcluded.mutate(
            { messageId, attachmentId: attachment.attachment_id, excluded },
            {
                onError: () => toast.error(tEmail("emailView.attachmentExcludeError", { defaultValue: "Operazione non riuscita" })),
            }
        );
    };

    return (
        <>
            <div
                className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border border-secondary bg-secondary/50 p-3 ${
                    excluded ? "opacity-70" : ""
                }`}
            >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                    {isImage && thumbnailUrl ? (
                        <button
                            type="button"
                            onClick={() => {
                                setPreviewUrl(thumbnailUrl);
                                setPreviewOpen(true);
                            }}
                            className="h-10 w-10 shrink-0 overflow-hidden rounded-lg border border-secondary bg-primary object-cover focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                            aria-label={tEmail("emailView.preview", { defaultValue: "Anteprima" })}
                        >
                            <img src={thumbnailUrl} alt="" className="h-full w-full object-cover" />
                        </button>
                    ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary">
                            <File06 className="h-5 w-5 text-tertiary" />
                        </div>
                    )}
                    <div className="min-w-0">
                        <p className={`text-sm font-medium text-primary ${excluded ? "line-through" : ""}`}>
                            {attachment.filename}
                        </p>
                        <p className="text-xs text-tertiary">
                            {formatFileSize(attachment.size_bytes)}
                            {attachment.content_type &&
                                ` • ${attachment.content_type.split("/")[1]?.toUpperCase() || attachment.content_type}`}
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {attachment.is_document && attachment.document_id && (
                        <span className="flex items-center gap-1 rounded-full bg-success-secondary px-2 py-1 text-xs text-success-primary">
                            <FileCheck02 className="h-3 w-3" />
                            {tEmail("emailView.elaborated", { defaultValue: "Elaborato" })}
                        </span>
                    )}
                    {excluded && (
                        <span className="rounded-full bg-secondary px-2 py-1 text-xs text-tertiary">
                            {tEmail("emailView.excluded", { defaultValue: "Escluso" })}
                        </span>
                    )}
                    <div className="flex items-center gap-1">
                        <Tooltip title={tEmail("emailView.excludeAttachment", { defaultValue: "Escludi" })} placement="top">
                            <Button
                                size="sm"
                                color="tertiary"
                                iconLeading={XCircle}
                                onPress={() => handleSetExcluded(true)}
                                isDisabled={setExcluded.isPending || excluded}
                                aria-label={tEmail("emailView.excludeAttachment", { defaultValue: "Escludi" })}
                            />
                        </Tooltip>
                        {hasFile && (
                            <>
                                {isImage && (
                                    <Tooltip title={tEmail("emailView.preview", { defaultValue: "Anteprima" })} placement="top">
                                    <Button
                                        size="sm"
                                        color="tertiary"
                                        iconLeading={Image01}
                                        onPress={() => (thumbnailUrl ? (setPreviewUrl(thumbnailUrl), setPreviewOpen(true)) : fetchAndOpen("preview"))}
                                        isDisabled={loadingUrl}
                                        aria-label={tEmail("emailView.preview", { defaultValue: "Anteprima" })}
                                    >
                                        {loadingUrl ? <LoadingIndicator type="line-spinner" size="sm" /> : null}
                                    </Button>
                                    </Tooltip>
                                )}
                                {isPdf && !isImage && (
                                    <Tooltip title={tEmail("emailView.openPdf", { defaultValue: "Apri PDF" })} placement="top">
                                    <Button
                                        size="sm"
                                        color="tertiary"
                                        iconLeading={Link03}
                                        onPress={() => fetchAndOpen("preview")}
                                        isDisabled={loadingUrl}
                                        aria-label={tEmail("emailView.openPdf", { defaultValue: "Apri PDF" })}
                                    >
                                        {loadingUrl ? <LoadingIndicator type="line-spinner" size="sm" /> : null}
                                    </Button>
                                    </Tooltip>
                                )}
                                <Tooltip title={tEmail("emailView.download", { defaultValue: "Download" })} placement="top">
                                <Button
                                    size="sm"
                                    color="tertiary"
                                    iconLeading={Download01}
                                    onPress={() => fetchAndOpen("download")}
                                    isDisabled={loadingUrl}
                                    aria-label={tEmail("emailView.download", { defaultValue: "Download" })}
                                >
                                    {loadingUrl ? <LoadingIndicator type="line-spinner" size="sm" /> : null}
                                </Button>
                                </Tooltip>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {previewOpen && previewUrl && isImage && (
                <ModalOverlay
                    isOpen={true}
                    onOpenChange={(open) => !open && (setPreviewOpen(false), setPreviewUrl(null))}
                    isDismissable
                >
                    <Modal className="w-max max-w-[90vw]">
                        <Dialog aria-label="Attachment preview" className="relative rounded-xl bg-primary p-2 shadow-lg ring-1 ring-secondary">
                            <Button
                                size="sm"
                                color="tertiary"
                                iconLeading={XClose}
                                aria-label={tEmail("emailView.closePreview", { defaultValue: "Chiudi" })}
                                onPress={() => {
                                    setPreviewOpen(false);
                                    setPreviewUrl(null);
                                }}
                                className="absolute top-2 right-2 z-10"
                            />
                            <img src={previewUrl} alt={attachment.filename} className="max-h-[85vh] w-auto object-contain" />
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </>
    );
}

export interface EmailDetailPanelProps {
    messageId: string | null;
    /** Optional placeholder when no message selected */
    emptyMessage?: string;
    /** Called when user closes the detail (e.g. icon button). Omit to hide close button. */
    onCloseDetail?: () => void;
}

export function EmailDetailPanel({ messageId, emptyMessage, onCloseDetail }: EmailDetailPanelProps) {
    const { t } = useTranslation(["pages:activity", "pages:email-settings"]);
    const tActivity = (key: string, opts?: { defaultValue?: string }) => t(key, { ns: "pages:activity", ...opts });
    const tEmail = (key: string, opts?: { defaultValue?: string }) => t(key, { ns: "pages:email-settings", ...opts });
    const [isMetadataExpanded, setIsMetadataExpanded] = useState(true);
    const [bodyHeight, setBodyHeight] = useState(DEFAULT_BODY_HEIGHT_PX);
    const bodyContainerRef = useRef<HTMLDivElement>(null);
    const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false);
    const [isMarkNotInContextModalOpen, setIsMarkNotInContextModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

    const markAsNotInContext = useMarkEmailAsNotInContext();
    const associateToContainer = useAssociateEmailToContainer();
    const reviewMessage = useReviewMessage();

    const handleResizeMove = useCallback((e: MouseEvent) => {
        const el = bodyContainerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const maxPx = window.innerHeight * 0.85;
        const newHeight = Math.min(maxPx, Math.max(MIN_BODY_HEIGHT_PX, e.clientY - rect.top));
        setBodyHeight(newHeight);
    }, []);
    const handleResizeEnd = useCallback(() => {
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, [handleResizeMove]);
    const handleResizeStart = useCallback(
        (e: React.MouseEvent) => {
            if (e.button !== 0) return;
            document.body.style.cursor = "ns-resize";
            document.body.style.userSelect = "none";
            document.addEventListener("mousemove", handleResizeMove);
            document.addEventListener("mouseup", handleResizeEnd);
        },
        [handleResizeMove, handleResizeEnd]
    );

    const { data: message, isLoading, error } = useEmailMessage(messageId || "", {
        enabled: !!messageId,
    });

    const handleConfirmMarkAsNotInContext = useCallback(() => {
        if (!messageId) return;
        markAsNotInContext.mutate(messageId, {
            onSuccess: () => {
                toast.success(tActivity("emailActions.markNotInContext.success", { defaultValue: "Email marcata come non in contesto" }));
                setIsMarkNotInContextModalOpen(false);
            },
            onError: () => {
                toast.error(tActivity("emailActions.markNotInContext.error", { defaultValue: "Errore nel marcare l'email" }));
            },
        });
    }, [messageId, markAsNotInContext, tActivity]);

    const handleAssociateToContainer = useCallback((containerId: string) => {
        if (!messageId) return;
        associateToContainer.mutate(
            { messageId, containerId },
            {
                onSuccess: () => {
                    toast.success(tActivity("emailActions.associateContainer.success", { defaultValue: "Email associata al container con successo" }));
                    setIsAssociateModalOpen(false);
                },
                onError: () => {
                    toast.error(tActivity("emailActions.associateContainer.error", { defaultValue: "Errore nell'associazione dell'email" }));
                },
            }
        );
    }, [messageId, associateToContainer, tActivity]);

    const handleApprove = useCallback((containerId?: string) => {
        if (!messageId) return;
        reviewMessage.mutate(
            { messageId, action: 'approve', containerId },
            {
                onSuccess: () => {
                    toast.success(
                        containerId 
                            ? tActivity("emailActions.approve.successWithContainer", { defaultValue: "Email approvata e associata al container con successo" })
                            : tActivity("emailActions.approve.success", { defaultValue: "Email approvata con successo" })
                    );
                    setIsApproveModalOpen(false);
                },
                onError: () => {
                    toast.error(tActivity("emailActions.approve.error", { defaultValue: "Errore nell'approvazione dell'email" }));
                },
            }
        );
    }, [messageId, reviewMessage, tActivity]);

    const handleReject = useCallback(() => {
        if (!messageId) return;
        reviewMessage.mutate(
            { messageId, action: 'reject' },
            {
                onSuccess: () => {
                    toast.success(tActivity("emailActions.reject.success", { defaultValue: "Email rifiutata con successo" }));
                },
                onError: () => {
                    toast.error(tActivity("emailActions.reject.error", { defaultValue: "Errore nel rifiuto dell'email" }));
                },
            }
        );
    }, [messageId, reviewMessage, tActivity]);

    if (!messageId) {
        return (
            <div className="flex h-full min-h-[200px] flex-col items-center justify-center rounded-xl border border-secondary bg-secondary/20 p-8 text-center text-tertiary">
                <Mail01 className="mb-2 h-10 w-10 text-tertiary/60" />
                <p className="text-sm">{emptyMessage ?? tActivity("tradeComms.selectEmail", { defaultValue: "Seleziona un'email per visualizzare il contenuto" })}</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-secondary bg-secondary/20">
                <LoadingIndicator type="line-spinner" size="md" />
            </div>
        );
    }

    if (error || !message) {
        return (
            <div className="rounded-xl border border-secondary bg-error-50 dark:bg-error-950/30 p-6 text-center text-error-600 dark:text-error-400">
                {tEmail("emailView.errorLoading", { defaultValue: "Errore nel caricamento dell'email" })}
            </div>
        );
    }

    // Determine if email is in limbo (needs manual review)
    const isLimboEmail = message.status === EmailMessageStatus.LIMBO;

    return (
        <div className="flex h-full flex-col overflow-auto rounded-xl border border-secondary bg-primary p-2">
            {/* Action Bar - different actions based on email status */}
            {isLimboEmail ? (
                // Azioni per email limbo: Approva/Rifiuta a sinistra, Chiudi dettaglio a destra
                <div className="mb-3 flex items-center justify-between gap-2 p-2 border-b border-secondary bg-warning-50 dark:bg-warning-950/30 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            color="tertiary"
                            iconLeading={XCircle}
                            onClick={handleReject}
                            isDisabled={reviewMessage.isPending}
                        >
                            {tActivity("emailActions.reject.label", { defaultValue: "Rifiuta" })}
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            iconLeading={CheckCircle}
                            onClick={() => setIsApproveModalOpen(true)}
                            isDisabled={reviewMessage.isPending}
                        >
                            {tActivity("emailActions.approve.label", { defaultValue: "Approva" })}
                        </Button>
                    </div>
                    {onCloseDetail && (
                        <Button
                            size="sm"
                            color="tertiary"
                            iconLeading={XClose}
                            onClick={onCloseDetail}
                            aria-label={tActivity("emailActions.closeDetail", { defaultValue: "Chiudi dettaglio" })}
                        />
                    )}
                </div>
            ) : (
                // Azioni per email normali: Non in contesto / Associa a sinistra, Chiudi dettaglio a destra
                <div className="mb-3 flex items-center justify-between gap-2 p-2 border-b border-secondary bg-secondary/20 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            color="tertiary"
                            iconLeading={AlertTriangle}
                            onClick={() => setIsMarkNotInContextModalOpen(true)}
                            isDisabled={markAsNotInContext.isPending}
                        >
                            {tActivity("emailActions.markNotInContext.label", { defaultValue: "Non in contesto" })}
                        </Button>
                        <Button
                            size="sm"
                            color="primary"
                            iconLeading={Link03}
                            onClick={() => setIsAssociateModalOpen(true)}
                            isDisabled={associateToContainer.isPending}
                        >
                            {tActivity("emailActions.associateContainer.label", { defaultValue: "Associa a Container" })}
                        </Button>
                    </div>
                    {onCloseDetail && (
                        <Button
                            size="sm"
                            color="tertiary"
                            iconLeading={XClose}
                            onClick={onCloseDetail}
                            aria-label={tActivity("emailActions.closeDetail", { defaultValue: "Chiudi dettaglio" })}
                        />
                    )}
                </div>
            )}
            
            <div className="mb-2 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-secondary">
                    <Mail01 className="h-5 w-5 text-brand-primary" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-semibold text-primary">{message.subject || tEmail("emailView.noSubject", { defaultValue: "Nessun oggetto" })}</h3>
                    <p className="truncate text-xs text-tertiary">
                        {message.from_name ? `${message.from_name} <${message.from_address}>` : message.from_address}
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div className="rounded-lg border border-secondary bg-secondary/30 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
                        className="flex w-full items-center justify-between p-2 transition-colors hover:bg-secondary/50"
                    >
                        <span className="text-sm font-medium text-tertiary">
                            {tEmail("emailView.details", { defaultValue: "Dettagli Email" })}
                        </span>
                        {isMetadataExpanded ? <ChevronUp className="h-4 w-4 text-tertiary" /> : <ChevronDown className="h-4 w-4 text-tertiary" />}
                    </button>
                    {isMetadataExpanded && (
                        <div className="border-t border-secondary px-2 pb-2 pt-1">
                            <dl className="space-y-2 text-sm">
                                <div className="flex gap-2">
                                    <dt className="w-16 shrink-0 font-medium text-tertiary">Da:</dt>
                                    <dd className="text-primary">{message.from_name ? `${message.from_name} <${message.from_address}>` : message.from_address || "—"}</dd>
                                </div>
                                {message.to_addresses?.length ? (
                                    <div className="flex gap-2">
                                        <dt className="w-16 shrink-0 font-medium text-tertiary">A:</dt>
                                        <dd className="text-primary">{message.to_addresses.join(", ")}</dd>
                                    </div>
                                ) : null}
                                <div className="flex gap-2">
                                    <dt className="w-16 shrink-0 font-medium text-tertiary">Data:</dt>
                                    <dd className="text-primary">{formatDate(message.received_at)}</dd>
                                </div>
                            </dl>
                        </div>
                    )}
                </div>

                {(message.body_text || message.body_html) && (
                    <div className="rounded-lg border border-secondary bg-primary p-2">
                        <h4 className="mb-1.5 text-sm font-medium text-tertiary">{tEmail("emailView.body", { defaultValue: "Corpo" })}</h4>
                        <div
                            ref={bodyContainerRef}
                            className="flex flex-col overflow-hidden rounded-lg"
                            style={{ height: bodyHeight, minHeight: MIN_BODY_HEIGHT_PX }}
                        >
                            <div className="min-h-0 flex-1 overflow-y-auto">
                                {message.body_html ? (
                                    <EmailBodyFrame
                                        html={extractBodyContent(message.body_html)}
                                        className="rounded-lg border border-secondary bg-white dark:bg-gray-50 h-full"
                                        height={bodyHeight - RESIZE_HANDLE_HEIGHT_PX}
                                    />
                                ) : (
                                    (() => {
                                        const bodyText = message.body_text || "";
                                        const isHtml = /<html|<\!DOCTYPE|<body|<div|<p>/i.test(bodyText);
                                        if (isHtml && bodyText.trim()) {
                                            return (
                                                <EmailBodyFrame
                                                    html={extractBodyContent(bodyText)}
                                                    className="rounded-lg border border-secondary bg-white dark:bg-gray-50 h-full"
                                                    height={bodyHeight - RESIZE_HANDLE_HEIGHT_PX}
                                                />
                                            );
                                        }
                                        return <pre className="whitespace-pre-wrap text-sm text-primary">{bodyText}</pre>;
                                    })()
                                )}
                            </div>
                            <button
                                type="button"
                                aria-label={tEmail("emailView.resizeBody", { defaultValue: "Ridimensiona area contenuto" })}
                                className="flex w-full cursor-ns-resize items-center justify-center border-t border-secondary bg-secondary/30 py-1.5 transition-colors hover:bg-secondary/50 focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                                onMouseDown={handleResizeStart}
                            >
                                <span className="text-tertiary" aria-hidden>
                                    ⋮⋮
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {message.attachments && message.attachments.length > 0 && (
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-tertiary" />
                            <span className="text-sm font-medium text-primary">
                                {tEmail("emailView.attachments", { defaultValue: "Allegati" })} ({message.attachments.length})
                            </span>
                        </div>
                        <div className="space-y-2">
                            {message.attachments.map((att) => (
                                <AttachmentItem
                                    key={att.attachment_id}
                                    messageId={message.message_id}
                                    attachment={att}
                                    tEmail={tEmail}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {!message.body_text && !message.body_html && (!message.attachments || message.attachments.length === 0) && (
                    <div className="rounded-lg bg-secondary/50 p-4 text-center text-sm text-tertiary">
                        {tEmail("emailView.noContent", { defaultValue: "Nessun contenuto" })}
                    </div>
                )}
            </div>
            
            {/* Associate Container Modal */}
            <AssociateContainerModal
                isOpen={isAssociateModalOpen}
                onClose={() => setIsAssociateModalOpen(false)}
                onConfirm={handleAssociateToContainer}
                isLoading={associateToContainer.isPending}
            />
            
            {/* Mark as Not in Context Modal */}
            <MarkNotInContextModal
                isOpen={isMarkNotInContextModalOpen}
                onClose={() => setIsMarkNotInContextModalOpen(false)}
                onConfirm={handleConfirmMarkAsNotInContext}
                isLoading={markAsNotInContext.isPending}
            />
            
            {/* Approve Email Modal (for limbo emails) */}
            <ApproveEmailModal
                isOpen={isApproveModalOpen}
                onClose={() => setIsApproveModalOpen(false)}
                onApprove={handleApprove}
                isLoading={reviewMessage.isPending}
            />
        </div>
    );
}
